import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Hash, Filter, Zap, ArrowLeft, Calendar, MapPin, Menu, Lock, ChevronDown, CheckCircle } from 'lucide-react';
import OrdemEntradaManual from './OrdemEntradaManual';
import OrdemEntradaFiltroNumeros from './OrdemEntradaFiltroNumeros';
import OrdemEntradaBaterias from './OrdemEntradaBaterias';
import leiloesService from '../../services/leiloesService';
import filtrosBateriasService from '../../services/filtrosBateriasService';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../hooks/useToast';
import { useSidebar } from '../../components/layout/Layout';

const OrdemEntrada = () => {
  const { leilaoId } = useParams();
  const { organizadorAtivo } = useOrganizador();
  const { user } = useAuthStore();
  const { setSidebarOpen } = useSidebar();
  const toast = useToast();
  const [metodoSelecionado, setMetodoSelecionado] = useState(() => {
    return localStorage.getItem(`metodo_ordem_${leilaoId}`) || null;
  });
  const [leilaoAtual, setLeilaoAtual] = useState(null);
  const [leiloes, setLeiloes] = useState([]);
  const [selectedLeilao, setSelectedLeilao] = useState('');
  const [loading, setLoading] = useState(true);

  const isPremium = (organizadorAtivo?.produtos_premium || []).includes('filtros_baterias');

  // Permissões de Ordem de Entrada do usuário atual
  const ordemPerm = (() => {
    const nivel = user?.permission_level;
    if (nivel === 'super_admin' || nivel === 'admin') {
      return { visualizar: true, editar_filtros: true, mover_manual: true, mover_manual_requer_justificativa: false };
    }
    const modulos = user?.modulos_acesso || [];
    const perm = modulos.find(m => m?.modulo === 'ordem_entrada');
    if (perm) return perm;
    // fallback para permissão simples de string (legado)
    const temOrdem = modulos.includes('ordem_entrada') || modulos.includes('ordem-entrada');
    return { visualizar: temOrdem, editar_filtros: temOrdem, mover_manual: temOrdem, mover_manual_requer_justificativa: false };
  })();

  useEffect(() => {
    if (organizadorAtivo?.id) carregarLeiloes();
  }, [organizadorAtivo]);

  useEffect(() => {
    if (selectedLeilao) carregarLeilaoEspecifico(selectedLeilao);
  }, [selectedLeilao]);

  useEffect(() => {
    if (leilaoId && organizadorAtivo?.id) carregarLeilaoEspecifico(leilaoId);
  }, [leilaoId, organizadorAtivo]);

  const carregarLeiloes = async () => {
    try {
      const data = await leiloesService.getByOrganizador(organizadorAtivo.id);
      setLeiloes(data || []);
      if (leilaoId) {
        setSelectedLeilao(leilaoId);
      } else if (data?.length > 0) {
        const recente = data.sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))[0];
        setSelectedLeilao(recente.id.toString());
      } else {
        setLoading(false);
      }
    } catch {
      toast.error('Erro ao carregar leilões');
      setLoading(false);
    }
  };

  const carregarLeilaoEspecifico = async (id) => {
    try {
      setLoading(true);
      const leilao = await leiloesService.getById(organizadorAtivo.id, id);
      setLeilaoAtual(leilao);
    } catch {
      toast.error('Erro ao carregar dados do leilão');
    } finally {
      setLoading(false);
    }
  };

  const voltar = () => {
    setMetodoSelecionado(null);
    localStorage.removeItem(`metodo_ordem_${selectedLeilao || leilaoId}`);
  };

  const metodos = [
    {
      id: 'manual',
      titulo: 'Ordem Manual',
      descricao: 'Organize os lotes arrastando e soltando ou usando setas',
      icone: Hash,
      cor: 'blue',
      disponivel: true,
    },
    {
      id: 'numerico',
      titulo: 'Filtro por Números',
      descricao: 'Configure regras automáticas baseadas em faixas numéricas',
      icone: Filter,
      cor: 'green',
      disponivel: true,
    },
    {
      id: 'baterias',
      titulo: 'Filtros Automáticos',
      descricao: 'Baterias por sexo ou regras personalizadas (tipo, raça, peso)',
      icone: Zap,
      cor: 'purple',
      disponivel: isPremium && ordemPerm.editar_filtros,
      premium: true,
      premiumAtivo: isPremium,
    },
  ];

  const LeilaoSelector = ({ cor = 'blue' }) => (
    <div className="min-w-0 sm:min-w-[280px]">
      <label className={`block text-sm font-medium text-${cor}-100 mb-2`}>Leilão</label>
      <select
        value={selectedLeilao || leilaoId}
        onChange={(e) => {
          setSelectedLeilao(e.target.value);
          window.location.href = `/ordem-entrada/${e.target.value}`;
        }}
        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm sm:text-base bg-white"
      >
        <option value="">Selecione um leilão...</option>
        {leiloes.map(l => (
          <option key={l.id} value={l.id}>
            {l.nome} - {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
          </option>
        ))}
      </select>
    </div>
  );

  const LeilaoInfo = () => leilaoAtual ? (
    <div className="text-right">
      <h2 className="text-lg font-semibold text-white">{leilaoAtual.nome}</h2>
      <div className="flex items-center gap-4 text-sm text-blue-100 mt-1">
        <span className="flex items-center gap-1"><Calendar size={14} />{new Date(leilaoAtual.data_leilao).toLocaleDateString('pt-BR')}</span>
        <span className="flex items-center gap-1"><MapPin size={14} />{leilaoAtual.cidade}, {leilaoAtual.uf}</span>
      </div>
    </div>
  ) : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!loading && leiloes.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-4 text-center">
        <div className="text-gray-400 text-5xl">📋</div>
        <p className="text-gray-600 font-semibold text-lg">Nenhum leilão cadastrado</p>
        <a href="/leiloes/novo" className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">
          Criar primeiro leilão
        </a>
      </div>
    );
  }

  const headerBase = (cor, titulo, descricao) => (
    <div className={`bg-gradient-to-r from-${cor}-600 to-${cor}-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6`}>
      <div className="flex items-center justify-between mb-4 md:hidden">
        <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl text-white hover:bg-white/20 transition">
          <Menu className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-semibold text-white truncate">{titulo}</h1>
        <div className="w-10" />
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <button onClick={voltar} className={`flex items-center gap-2 text-${cor}-100 hover:text-white transition mb-2`}>
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Voltar aos métodos</span>
          </button>
          <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">{titulo}</h1>
          {descricao && <p className={`text-${cor}-100 mt-1 text-sm sm:text-base hidden sm:block`}>{descricao}</p>}
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <LeilaoSelector cor={cor} />
          <LeilaoInfo />
        </div>
      </div>
    </div>
  );

  if (metodoSelecionado === 'numerico') {
    return (
      <div className="space-y-4 sm:space-y-6">
        {headerBase('green', 'Filtro por Números', 'Defina faixas numéricas e a ordem de entrada de cada grupo')}
        <OrdemEntradaFiltroNumeros leilaoId={selectedLeilao || leilaoId} organizadorId={organizadorAtivo?.id} />
      </div>
    );
  }

  if (metodoSelecionado === 'manual') {
    return (
      <div className="space-y-4 sm:space-y-6">
        {headerBase('blue', 'Ordem Manual', 'Organize os lotes arrastando e soltando')}
        <OrdemEntradaManual
          leilaoId={selectedLeilao || leilaoId}
          podeMoverManual={ordemPerm.mover_manual}
          requerJustificativa={ordemPerm.mover_manual_requer_justificativa}
          organizadorId={organizadorAtivo?.id}
        />
      </div>
    );
  }

  if (metodoSelecionado === 'baterias') {
    return (
      <div className="space-y-4 sm:space-y-6">
        {headerBase('purple', 'Filtros Automáticos', 'Gere a ordem por baterias com filtros inteligentes')}
        <OrdemEntradaBaterias
          leilaoId={selectedLeilao || leilaoId}
          organizadorId={organizadorAtivo?.id}
          leilaoAtual={leilaoAtual}
          onHorarioSalvo={(d) => setLeilaoAtual(l => ({ ...l, ...d }))}
          podeEditar={ordemPerm.editar_filtros}
          podeMoverManual={ordemPerm.mover_manual}
          requerJustificativa={ordemPerm.mover_manual_requer_justificativa}
        />
      </div>
    );
  }

  // Tela de seleção de método
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between mb-4 md:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl text-white hover:bg-white/20 transition">
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-semibold text-white truncate">Ordem de Entrada</h1>
          <div className="w-10" />
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">Ordem de Entrada</h1>
            <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">Escolha o método para definir a sequência dos lotes</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <LeilaoSelector />
            <LeilaoInfo />
          </div>
        </div>
      </div>

      {selectedLeilao && leilaoAtual ? (
        <>
          {/* Horário limite info */}
          {leilaoAtual.horario_limite_entrada && (
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-700">
              <span>⏰</span>
              <span>Horário limite de entrada configurado: <strong>{leilaoAtual.horario_limite_entrada}</strong> — lotes cadastrados após esse horário serão marcados como pós-horário e irão ao fim da ordem.</span>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {metodos.map((metodo) => {
                const Icone = metodo.icone;
                const bloqueado = metodo.premium && !metodo.premiumAtivo;

                return (
                  <div
                    key={metodo.id}
                    className={`relative bg-white rounded-xl border-2 transition-all duration-200 ${
                      bloqueado
                        ? 'border-gray-100 opacity-60 cursor-not-allowed'
                        : metodo.disponivel
                        ? 'border-gray-200 hover:border-blue-300 hover:shadow-lg cursor-pointer'
                        : 'border-gray-100 opacity-60 cursor-not-allowed'
                    }`}
                    onClick={() => {
                      if (!bloqueado && metodo.disponivel) {
                        setMetodoSelecionado(metodo.id);
                        localStorage.setItem(`metodo_ordem_${selectedLeilao || leilaoId}`, metodo.id);
                      }
                    }}
                  >
                    {metodo.premium && (
                      <div className={`absolute -top-2 -right-2 text-white text-xs font-bold px-3 py-1 rounded-full ${
                        metodo.premiumAtivo ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-400'
                      }`}>
                        {metodo.premiumAtivo ? 'PREMIUM' : 'BLOQUEADO'}
                      </div>
                    )}

                    <div className="p-6">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                        metodo.cor === 'blue' ? 'bg-blue-100' : metodo.cor === 'green' ? 'bg-green-100' : 'bg-purple-100'
                      }`}>
                        {bloqueado
                          ? <Lock size={24} className="text-gray-400" />
                          : <Icone size={24} className={
                              metodo.cor === 'blue' ? 'text-blue-600' : metodo.cor === 'green' ? 'text-green-600' : 'text-purple-600'
                            } />
                        }
                      </div>

                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{metodo.titulo}</h3>
                      <p className="text-gray-600 text-sm mb-4">{metodo.descricao}</p>

                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${
                          bloqueado ? 'text-gray-400' :
                          metodo.disponivel ?
                            metodo.cor === 'blue' ? 'text-blue-600' : metodo.cor === 'green' ? 'text-green-600' : 'text-purple-600'
                          : 'text-gray-400'
                        }`}>
                          {bloqueado ? 'Requer plano Premium' : metodo.disponivel ? 'Disponível' : 'Sem permissão'}
                        </span>

                        {!bloqueado && metodo.disponivel && (
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            metodo.cor === 'blue' ? 'bg-blue-600' : metodo.cor === 'green' ? 'bg-green-600' : 'bg-purple-600'
                          }`}>
                            <ArrowLeft size={16} className="text-white rotate-180" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
          <Hash size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {selectedLeilao ? 'Carregando leilão...' : 'Selecione um leilão'}
          </h3>
          <p className="text-gray-600">
            {selectedLeilao ? 'Aguarde enquanto carregamos os dados' : 'Escolha um leilão para configurar a ordem de entrada'}
          </p>
        </div>
      )}
    </div>
  );
};

export { OrdemEntrada };
export default OrdemEntrada;
